import { useCallback, useEffect, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { SingleSelectOption } from '@/modules/common';
import { DEFAULT_CURRENCY_CODE } from '@/modules/common/constants/currency.constants';
import { useCurrencies, useOrganizationDefaults } from '@/modules/common/hooks';
import { useFeatureFlags } from '@/modules/common/hooks/use-feature-flags';
import { RateSource } from '@/modules/common/types/rate-source';
import { PaymentDetailsCard } from '@/modules/settings/components/payment-details/payment-details-card';
import { RateConfigurationCard } from '@/modules/settings/components/rate-configuration/rate-configuration-card';
import { SettingsPageWrapper } from '@/modules/settings/components/settings-page-wrapper';
import { useUpdateExternalRates } from '@/modules/settings/hooks/external-rates/use-update-external-rates';
import { SettingsOutletContext } from '@/modules/settings/types/settings-footer';
import {
  type ExternalRatesFormValues,
  externalRatesSchema,
} from '@/modules/settings/validations/external-rates-schema';

// Only show these 4 rate source options per API contract
const RATE_SOURCE_OPTIONS: RateSource[] = [
  RateSource.GlobalDefaultRate,
  RateSource.UserExternalRate,
  // TODO: Post-MVP - uncomment
  // RateSource.RoleBasedRate,
  // RateSource.SeniorityBasedRate,
];

export const FinancePage = () => {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const { setFooterState } = useOutletContext<SettingsOutletContext>();
  const { showPaymentDetails } = useFeatureFlags();

  const { currencies, isLoadingCurrencies, currenciesError } = useCurrencies();

  const {
    data: defaults,
    isLoading: isLoadingDefaults,
    error: defaultsError,
    refetch: refetchDefaults,
  } = useOrganizationDefaults({ enabled: currencies.length > 0 });

  const updateMutation = useUpdateExternalRates();

  const form = useForm<ExternalRatesFormValues>({
    resolver: zodResolver(externalRatesSchema),
    mode: 'onChange',
    defaultValues: {
      defaultCurrencyCode: '',
      defaultProjectRateSource: RateSource.GlobalDefaultRate,
      defaultRate: 0,
    },
  });

  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: form.formState.isDirty,
    onDiscard: () => form.reset(),
  });

  const unsavedChangesGuardProps = useMemo(
    () => ({
      isModalOpen: unsavedChangesGuard.isModalOpen,
      onDiscard: unsavedChangesGuard.handleModalDiscard,
      onCancel: unsavedChangesGuard.handleModalCancel,
    }),
    [
      unsavedChangesGuard.isModalOpen,
      unsavedChangesGuard.handleModalDiscard,
      unsavedChangesGuard.handleModalCancel,
    ]
  );

  // Initialize form values when data loads
  useEffect(() => {
    if (defaults && currencies.length > 0) {
      const currencyCode = defaults.currency?.code || DEFAULT_CURRENCY_CODE;
      const rateSource = defaults.projectRateSource || RateSource.GlobalDefaultRate;
      const rate = defaults.organizationRate ?? 0;

      form.reset({
        defaultCurrencyCode: currencyCode,
        defaultProjectRateSource: rateSource,
        defaultRate: rate,
      });
    }
  }, [defaults, currencies, form]);

  const handleSave = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    await updateMutation.mutateAsync(values);
    await refetchDefaults();
  }, [form, updateMutation, refetchDefaults]);

  // Currency options
  const currencyOptions: SingleSelectOption[] = useMemo(
    () =>
      currencies.map(currency => ({
        value: currency.code,
        label: currency.toString(),
      })),
    [currencies]
  );

  // Rate source options
  const rateSourceOptions: SingleSelectOption[] = useMemo(
    () =>
      RATE_SOURCE_OPTIONS.map(source => ({
        value: source,
        label: t(`authenticated.settings:pages.finance.rateSource.${source.toLowerCase()}`),
      })),
    [t]
  );

  // Update footer state
  useEffect(() => {
    const hasErrors = Object.keys(form.formState.errors).length > 0;
    const isFormValid = form.formState.isValid && !hasErrors;

    setFooterState({
      isVisible: true,
      isEnabled: form.formState.isDirty && isFormValid,
      isLoading: updateMutation.isPending,
      onSave: handleSave,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.formState.isDirty,
    form.formState.isValid,
    form.formState.errors,
    updateMutation.isPending,
  ]);

  // Cleanup footer on unmount
  useEffect(() => {
    return () => {
      setFooterState({
        isVisible: false,
        isEnabled: false,
        isLoading: false,
        onSave: () => {},
      });
    };
  }, [setFooterState]);

  const isLoading = isLoadingDefaults || isLoadingCurrencies;
  const error = defaultsError || currenciesError;

  return (
    <SettingsPageWrapper
      isLoading={isLoading}
      error={error}
      unsavedChangesGuard={unsavedChangesGuardProps}
    >
      <RateConfigurationCard
        form={form}
        isLoading={isLoading}
        hasData={Boolean(defaults) && currencies.length > 0}
        currencyOptions={currencyOptions}
        rateSourceOptions={rateSourceOptions}
      />

      {showPaymentDetails && (
        <div className="mt-6">
          <PaymentDetailsCard />
        </div>
      )}
    </SettingsPageWrapper>
  );
};
